import React, { useState, useEffect } from 'react';
import {
  Menu,
  X,
  ChevronRight,
  Home,
  Users,
  MessageCircle,
  Bell,
  CheckCircle,
  Info,
  Lightbulb,
  Pin,
  Search,
  Filter,
  Settings,
  Eye,
  Send,
  Smartphone,
  Globe,
  Clock,
  Target,
  BarChart3,
  Zap,
  Shield
} from 'lucide-react';

// Platform icons as SVG components since we can't import external icons
const FacebookIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const InstagramIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="url(#instagram-gradient)">
    <defs>
      <linearGradient id="instagram-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f09433" />
        <stop offset="25%" stopColor="#e6683c" />
        <stop offset="50%" stopColor="#dc2743" />
        <stop offset="75%" stopColor="#cc2366" />
        <stop offset="100%" stopColor="#bc1888" />
      </linearGradient>
    </defs>
    <path d="M16.98 0a6.9 6.9 0 0 1 6.9 6.9v10.2a6.9 6.9 0 0 1-6.9 6.9H7.02a6.9 6.9 0 0 1-6.9-6.9V6.9A6.9 6.9 0 0 1 7.02 0h9.96zM12 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#25D366">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.525 3.488" />
  </svg>
);

const ManagingDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', title: 'Dashboard Overview', icon: Home },
    { id: 'profiles', title: 'Viewing Linked Profiles', icon: Users },
    { id: 'messages', title: 'Managing Messages & Chats', icon: MessageCircle },
    { id: 'notifications', title: 'Real-Time Notifications', icon: Bell },
    { id: 'platform-features', title: 'Platform-Specific Features', icon: Target },
    { id: 'advanced-features', title: 'Advanced Features', icon: Zap }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setSidebarOpen(false);
  };

  const ExampleBox = ({ children }) => (
    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4 rounded-r-lg">
      <div className="flex items-start">
        <Info className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-blue-800 mb-1">Example</p>
          <div className="text-blue-700 text-sm">{children}</div>
        </div>
      </div>
    </div>
  );

  const ProTip = ({ children }) => (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 rounded-r-lg">
      <div className="flex items-start">
        <Lightbulb className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-yellow-800 mb-1">Pro Tip</p>
          <div className="text-yellow-700 text-sm">{children}</div>
        </div>
      </div>
    </div>
  );

  const PlatformCard = ({ icon: Icon, name, description, features }) => (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6 border-l-4 border-blue-400">
      <div className="flex items-center mb-4">
        <Icon />
        <h4 className="text-lg font-semibold ml-3">{name}</h4>
      </div>
      <p className="text-gray-600 mb-4">{description}</p>
      <ul className="space-y-2">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-gray-700">{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 mt-[32px]">
      <div className="flex">
        {/* Main Content */}
        <div className="flex-1 lg:ml-0">
          <main className="">
            <div className="">
              {/* Introduction */}
              <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
                <div className="text-left">
                  <h1 className="text-2xl font-bold text-gray-900 mb-6">
                    Managing Your Dashboard: Your Control Center for Success
                  </h1>
                  <p className="text-lg text-gray-600 mx-auto">
                    Welcome to your dashboard—your central hub for managing all your social media platforms with ease!
                    The dashboard is designed to be intuitive, efficient, and packed with features to streamline
                    your communication and engagement across Facebook, Instagram, WhatsApp, and Telegram.
                  </p>
                </div>
              </div>



              {/* Dashboard Overview Section */}
              <section id="overview" className="mb-12">
                <div className="bg-white rounded-lg shadow-sm p-8">
                  <div className="flex items-center mb-6">
                    <div className="bg-blue-100 rounded-lg p-2 mr-4">
                      <Home className="w-6 h-6 text-blue-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview: Your Mission Control</h2>
                  </div>

                  <p className="text-gray-600 mb-6">
                    The dashboard provides a unified view of all your connected platforms, giving you a bird's-eye view of your business activity.
                    Here's what you'll see when you log in:
                  </p>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-gray-50 rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <Globe className="w-5 h-5 text-blue-600 mr-2" />
                        <h3 className="text-lg font-semibold text-gray-900">Platform Overview</h3>
                      </div>
                      <p className="text-gray-600 mb-4">
                        At a glance, view which platforms (Facebook, Instagram, WhatsApp, and Telegram) are connected.
                      </p>
                      <div className="flex space-x-2">
                        <FacebookIcon />
                        <InstagramIcon />
                        <WhatsAppIcon />
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <Clock className="w-5 h-5 text-blue-600 mr-2" />
                        <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
                      </div>
                      <p className="text-gray-600 mb-4">
                        A live feed of recent messages, interactions, and notifications helps you stay updated.
                      </p>
                      <div className="flex items-center">
                        <Bell className="w-4 h-4 text-green-500 mr-2" />
                        <span className="text-sm text-gray-700">Real-time updates</span>
                      </div>
                    </div>
                  </div>

                  <ExampleBox>
                    A green checkmark next to the platform icon indicates it's active and synced.
                    You received 5 new messages on Instagram and 3 WhatsApp inquiries.
                  </ExampleBox>

                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="flex items-center">
                        <Send className="w-4 h-4 text-blue-600 mr-2" />
                        <span className="text-sm text-gray-700">Start a new chat</span>
                      </div>
                      <div className="flex items-center">
                        <Eye className="w-4 h-4 text-blue-600 mr-2" />
                        <span className="text-sm text-gray-700">View unread messages</span>
                      </div>
                      <div className="flex items-center">
                        <Settings className="w-4 h-4 text-blue-600 mr-2" />
                        <span className="text-sm text-gray-700">Access settings</span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Viewing Linked Profiles Section */}
              <section id="profiles" className="mb-12">
                <div className="bg-white rounded-lg shadow-sm p-8">
                  <div className="flex items-center mb-6">
                    <div className="bg-green-100 rounded-lg p-2 mr-4">
                      <Users className="w-6 h-6 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Viewing Linked Profiles</h2>
                  </div>

                  <p className="text-gray-600 mb-6">
                    Once you've linked your platforms, your dashboard becomes the central place to manage profiles across all social media channels.
                  </p>

                  <div className="space-y-6">
                    <div className="border-l-4 border-green-400 pl-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Instagram & Facebook Profiles</h3>
                      <ul className="space-y-2 text-gray-600">
                        <li className="flex items-start">
                          <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-green-500" />
                          View details like your profile picture, bio, followers, and post engagement stats
                        </li>
                        <li className="flex items-start">
                          <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-green-500" />
                          Update your business name, bio, or other details directly from the dashboard
                        </li>
                        <li className="flex items-start">
                          <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-green-500" />
                          Monitor engagement metrics and audience insights
                        </li>
                      </ul>
                    </div>

                    <div className="border-l-4 border-blue-400 pl-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">WhatsApp & Telegram Profiles</h3>
                      <ul className="space-y-2 text-gray-600">
                        <li className="flex items-start">
                          <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-blue-500" />
                          Manage all incoming and outgoing messages seamlessly
                        </li>
                        <li className="flex items-start">
                          <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-blue-500" />
                          Set up automated responses and quick replies
                        </li>
                        <li className="flex items-start">
                          <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-blue-500" />
                          Track message delivery and read receipts
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6 mt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">How to Access Profiles</h3>
                    <ol className="space-y-3 text-gray-600">
                      <li className="flex items-start">
                        <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">1</span>
                        On the sidebar, click <strong>Profiles</strong>
                      </li>
                      <li className="flex items-start">
                        <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">2</span>
                        Select the platform (e.g., Instagram, Facebook) to view and manage the corresponding profile
                      </li>
                      <li className="flex items-start">
                        <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">3</span>
                        Make updates as needed, and they'll sync automatically to your accounts
                      </li>
                    </ol>
                  </div>
                </div>
              </section>

              {/* Managing Messages Section */}
              <section id="messages" className="mb-12">
                <div className="bg-white rounded-lg shadow-sm p-8">
                  <div className="flex items-center mb-6">
                    <div className="bg-purple-100 rounded-lg p-2 mr-4">
                      <MessageCircle className="w-6 h-6 text-purple-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Managing Messages and Chats: Communication Made Easy</h2>
                  </div>

                  <p className="text-gray-600 mb-6">
                    The heart of the dashboard is the Messages section, where you can engage with your audience in real time across all platforms.
                  </p>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Unified Inbox</h3>
                      <p className="text-gray-600 mb-4">
                        All messages from Facebook, Instagram, WhatsApp, and Telegram are displayed in one inbox,
                        so you never miss a conversation.
                      </p>
                      <div className="flex items-center space-x-2">
                        <FacebookIcon />
                        <InstagramIcon />
                        <WhatsAppIcon />
                        <span className="text-sm text-gray-500">→ One Inbox</span>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Real-Time Messaging</h3>
                      <ul className="space-y-2 text-gray-600 text-sm">
                        <li className="flex items-center">
                          <Zap className="w-4 h-4 text-blue-500 mr-2" />
                          Respond instantly to inquiries
                        </li>
                        <li className="flex items-center">
                          <Send className="w-4 h-4 text-blue-500 mr-2" />
                          Send follow-ups and updates
                        </li>
                        <li className="flex items-center">
                          <Smartphone className="w-4 h-4 text-blue-500 mr-2" />
                          Add media files and attachments
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Search className="w-5 h-5 mr-2 text-gray-600" />
                      Search and Filter
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Search Capabilities</h4>
                        <ul className="space-y-1 text-sm text-gray-600">
                          <li>• Find conversations by user name</li>
                          <li>• Search by keywords</li>
                          <li>• Date range filtering</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Filter Options</h4>
                        <ul className="space-y-1 text-sm text-gray-600">
                          <li>• Filter by platform</li>
                          <li>• Unread status</li>
                          <li>• Priority messages</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <ProTip>
                    Pin important chats to keep them at the top of your inbox for quick access. Use the pin icon next to any conversation to prioritize it.
                  </ProTip>
                </div>
              </section>

              {/* Real-Time Notifications Section */}
              {/* <section id="notifications" className="mb-12">
                <div className="bg-white rounded-lg shadow-sm p-8">
                  <div className="flex items-center mb-6">
                    <div className="bg-yellow-100 rounded-lg p-2 mr-4">
                      <Bell className="w-6 h-6 text-yellow-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Real-Time Notifications: Never Miss a Beat</h2>
                  </div>

                  <p className="text-gray-600 mb-6">
                    Stay on top of your communication with instant alerts for new messages and activities across all your connected platforms.
                  </p>

                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">How Notifications Work</h3>
                      <div className="space-y-4">
                        <div className="flex items-start">
                          <Target className="w-5 h-5 text-orange-600 mr-3 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-gray-900">Platform-Specific Notifications</h4>
                            <p className="text-gray-600 text-sm">Customize your notifications by platform and message type</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <ExampleBox>
                      "John Doe just sent you a message on WhatsApp!" - You'll receive instant notifications like this with the sender's name and platform.
                    </ExampleBox>

                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Customize Your Notifications</h3>
                      <ol className="space-y-3 text-gray-600">
                        <li className="flex items-start">
                          <span className="bg-yellow-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">1</span>
                          Click <strong>Settings</strong> in the sidebar
                        </li>
                        <li className="flex items-start">
                          <span className="bg-yellow-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">2</span>
                          Go to the <strong>Notifications</strong> tab
                        </li>
                        <li className="flex items-start">
                          <span className="bg-yellow-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">3</span>
                          Adjust preferences by platform or message type
                        </li>
                      </ol>
                    </div>

                    <ProTip>
                      Set different notification schedules for each platform. For example, receive Instagram message alerts immediately but silence Facebook notifications during non-business hours.
                    </ProTip>
                  </div>
                </div>
              </section> */}

              {/* Platform-Specific Features Section */}
              <section id="platform-features" className="mb-12">
                <div className="bg-white rounded-lg shadow-sm p-8">
                  <div className="flex items-center mb-6">
                    <div className="bg-indigo-100 rounded-lg p-2 mr-4">
                      <Target className="w-6 h-6 text-indigo-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Platform-Specific Features</h2>
                  </div>

                  <p className="text-gray-600 mb-8">
                    Each platform offers unique capabilities that our dashboard leverages to maximize your business communication effectiveness.
                  </p>

                  <div className="space-y-6">
                    <PlatformCard
                      icon={FacebookIcon}
                      name="Facebook Business Integration"
                      description="Comprehensive business communication with advanced engagement tools and analytics for your Facebook business presence."
                      features={[
                        "Manage Facebook Messenger conversations with customers",
                        "Access page insights and engagement metrics",
                        "Schedule and publish posts directly from dashboard",
                        "Handle comments and reviews efficiently",
                        "Set up automated welcome messages and FAQ responses",
                        "Integration with Facebook Business Suite for enhanced analytics"
                      ]}
                    />

                    <PlatformCard
                      icon={InstagramIcon}
                      name="Instagram Business Suite"
                      description="Professional Instagram management with focus on visual content and direct messaging for business growth."
                      features={[
                        "Manage Instagram Direct Messages and story replies",
                        "Track follower growth and engagement rates",
                        "Schedule Instagram posts and stories",
                        "Monitor hashtag performance and reach",
                        "Respond to comments and manage user-generated content",
                        "Access Instagram Shopping integration and product tags"
                      ]}
                    />

                    <PlatformCard
                      icon={WhatsAppIcon}
                      name="WhatsApp Business API"
                      description="Professional WhatsApp communication with advanced business features for customer support and marketing."
                      features={[
                        "WhatsApp Business API integration for official business messaging",
                        "Automated welcome messages and quick replies",
                        "Broadcast lists for marketing campaigns (within compliance)",
                        "Message templates for common business communications",
                        "Read receipts and delivery confirmations",
                        "Customer support ticketing through WhatsApp conversations"
                      ]}
                    />
                  </div>

                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 mt-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Shield className="w-5 h-5 mr-2 text-indigo-600" />
                      Compliance & Security
                    </h3>
                    <p className="text-gray-600 mb-4">
                      All platform integrations maintain compliance with respective platform policies and data protection regulations.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Data Security</h4>
                        <ul className="space-y-1 text-gray-600">
                          <li>• End-to-end encryption where supported</li>
                          <li>• Secure API connections</li>
                          <li>• Regular security audits</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Platform Compliance</h4>
                        <ul className="space-y-1 text-gray-600">
                          <li>• GDPR and privacy regulation compliance</li>
                          <li>• Platform terms of service adherence</li>
                          <li>• Business verification requirements</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Advanced Features Section */}
              <section id="advanced-features" className="mb-12">
                <div className="bg-white rounded-lg shadow-sm p-8">
                  <div className="flex items-center mb-6">
                    <div className="bg-emerald-100 rounded-lg p-2 mr-4">
                      <Zap className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Advanced Features</h2>
                  </div>

                  <p className="text-gray-600 mb-8">
                    Take your communication management to the next level with these powerful advanced features designed for growing businesses.
                  </p>

                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <BarChart3 className="w-6 h-6 text-emerald-600 mr-3" />
                        <h3 className="text-lg font-semibold text-gray-900">Analytics & Reporting</h3>
                      </div>
                      <ul className="space-y-2 text-sm text-gray-600">
                        <li>• Response time analytics across platforms</li>
                        <li>• Customer engagement metrics and trends</li>
                        <li>• Message volume and peak activity tracking</li>
                        <li>• Conversion tracking from conversations to sales</li>
                        <li>• Custom reporting dashboards</li>
                      </ul>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <Zap className="w-6 h-6 text-blue-600 mr-3" />
                        <h3 className="text-lg font-semibold text-gray-900">Automation Tools</h3>
                      </div>
                      <ul className="space-y-2 text-sm text-gray-600">
                        <li>• AI-powered auto-responses</li>
                        <li>• Workflow automation for common tasks</li>
                        <li>• Scheduled message sending</li>
                        <li>• Lead qualification automation</li>
                        <li>• Integration with CRM systems</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Collaboration Features</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="bg-white rounded-lg p-4 mb-3 shadow-sm">
                          <Users className="w-8 h-8 text-blue-600 mx-auto" />
                        </div>
                        <h4 className="font-medium text-gray-900 mb-2">Team Management</h4>
                        <p className="text-sm text-gray-600">Assign conversations to team members and track performance</p>
                      </div>
                      <div className="text-center">
                        <div className="bg-white rounded-lg p-4 mb-3 shadow-sm">
                          <MessageCircle className="w-8 h-8 text-green-600 mx-auto" />
                        </div>
                        <h4 className="font-medium text-gray-900 mb-2">Internal Notes</h4>
                        <p className="text-sm text-gray-600">Add private notes to customer conversations for team context</p>
                      </div>
                      <div className="text-center">
                        <div className="bg-white rounded-lg p-4 mb-3 shadow-sm">
                          <Bell className="w-8 h-8 text-yellow-600 mx-auto" />
                        </div>
                        <h4 className="font-medium text-gray-900 mb-2">Escalation Rules</h4>
                        <p className="text-sm text-gray-600">Automatically escalate urgent messages to supervisors</p>
                      </div>
                    </div>
                  </div>

                  <ExampleBox>
                    Set up an automation rule: "When a customer mentions 'urgent' or 'complaint', immediately notify the manager and tag the conversation as high priority."
                  </ExampleBox>
                </div>
              </section>

              {/* Getting Started Section */}
              {/* <section className="mb-12">
                <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-lg shadow-lg p-8 text-white">
                  <div className="text-center">
                    <div className="bg-white bg-opacity-20 rounded-full p-3 w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
                    <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
                      After completing the setup steps above, your Facebook, Instagram, WhatsApp, and Telegram will be connected, 
                      and you can manage all interactions directly through your unified social media platform dashboard.
                    </p>
                    <div className="bg-white bg-opacity-10 rounded-lg p-6 text-left max-w-2xl mx-auto">
                      <h3 className="text-lg font-semibold mb-4">Next Steps:</h3>
                      <ol className="space-y-2 text-blue-100">
                        <li className="flex items-start">
                          <span className="bg-white bg-opacity-20 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">1</span>
                          Connect your first platform using the setup wizard
                        </li>
                        <li className="flex items-start">
                          <span className="bg-white bg-opacity-20 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">2</span>
                          Customize your notification preferences
                        </li>
                        <li className="flex items-start">
                          <span className="bg-white bg-opacity-20 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">3</span>
                          Set up automated responses for common questions
                        </li>
                        <li className="flex items-start">
                          <span className="bg-white bg-opacity-20 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">4</span>
                          Invite team members and assign roles
                        </li>
                      </ol>
                    </div>
                  </div>
                </div>
              </section> */}

              {/* Footer */}
              <footer className="bg-white rounded-lg shadow-sm p-6 text-center">
                <p className="text-gray-600 mb-4">
                  Need additional help? Our support team is here to assist you with any questions about managing your dashboard.
                </p>
                <div className="flex justify-center space-x-4 text-sm">
                  <a href="#" className="text-blue-600 hover:text-blue-800 transition-colors">Support Center</a>
                  <span className="text-gray-300">|</span>
                  <a href="#" className="text-blue-600 hover:text-blue-800 transition-colors">Video Tutorials</a>
                  <span className="text-gray-300">|</span>
                  <a href="#" className="text-blue-600 hover:text-blue-800 transition-colors">Contact Us</a>
                </div>
              </footer>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ManagingDashboard;
